import {
  getRemoveFromSessionBlockReason,
  canRemovePlayerFromSession,
} from './playerUtils';

export const PREF_DISABLED_TOOLTIP =
  'Turn on Match preferences in Manage → Match settings to add or change a preference for this session.';

export const PREF_MEMBER_SELF_SERVICE_TOOLTIP =
  'Members set their own game preference in the You tab. Hosts can only add preferences for guests.';

export const EDIT_PLAYER_GUEST_ONLY_TOOLTIP =
  'Only guest players can be edited by the host. Members manage their own profile.';

export const MENS_ELIGIBILITY_FEMALE_ONLY_TOOLTIP =
  'Mens doubles eligibility applies to female players only.';

export const PAUSE_BLOCKED_ON_COURT_MSG =
  'Remove them from the court or finish the match first.';

/** Menu item keys that may be exposed as dedicated row buttons on Courts tab. */
export const HOST_MENU_EXCLUDE_KEYS = {
  pause: 'pause',
  remove: 'remove',
  addToWaitlist: 'addToWaitlist',
  resume: 'resume',
};

const dividerBeforeStatusActions = (topSection) => {
  if (topSection.length === 0) return [];
  const last = topSection[topSection.length - 1];
  if (last && last.divider) return [];
  return [{ divider: true }];
};

const filterExcludedItems = (items, excludeKeys = []) => {
  if (!excludeKeys.length) return items;
  const exclude = new Set(excludeKeys);
  return items.filter((item) => {
    if (item.divider) return true;
    if (!item.menuKey) return true;
    return !exclude.has(item.menuKey);
  });
};

/** Collapse duplicate consecutive dividers and trim leading/trailing dividers. */
const normalizeMenuItems = (items) => {
  const result = [];
  items.forEach((item) => {
    if (item.divider) {
      if (result.length === 0 || result[result.length - 1]?.divider) return;
      result.push(item);
      return;
    }
    result.push(item);
  });
  while (result.length > 0 && result[result.length - 1]?.divider) {
    result.pop();
  }
  return result;
};

/**
 * Build DropdownMenu items for competitive session host player actions.
 *
 * @param {object} player
 * @param {object} context
 * @param {object} [options]
 * @param {string[]} [options.excludeKeys] - HOST_MENU_EXCLUDE_KEYS values to omit (Courts row buttons)
 */
export const getCompetitiveHostMenuItems = (player, context, options = {}) => {
  const {
    sessionId,
    sessionPlayerStatus = {},
    sessionPlayerExitReasons = {},
    sessionPlayerScheduledGames = {},
    partnershipLookup = {},
    playersById = {},
    playersOnActiveCourts = null,
    playersInUpcomingCourts = null,
    matchPreferencesEnabled = true,
    hideMatchPreferenceMenu = false,
    hideLateJoinPenaltyMenu = false,
    hideMensDoublesEligibilityMenu = false,
    togglePlayerStatus,
    openPlayerInfoModal,
    setPreferenceModalPlayer,
    setEligibilityModalPlayer,
    setRemoveModalPlayer,
  } = context;

  const { excludeKeys = [] } = options;
  const status = sessionPlayerStatus[player.id];
  const exitReason = sessionPlayerExitReasons[player.id];

  const isOnActiveCourt = (playerId) =>
    playersOnActiveCourts &&
    typeof playersOnActiveCourts.has === 'function' &&
    playersOnActiveCourts.has(playerId);

  const isInUpcomingCourt = (playerId) =>
    playersInUpcomingCourts &&
    typeof playersInUpcomingCourts.has === 'function' &&
    playersInUpcomingCourts.has(playerId);

  const buildRemoveMenuItem = () => {
    const blockDesc = getRemoveFromSessionBlockReason({
      player,
      isOnCourt: isOnActiveCourt(player.id),
      isInUpcoming: isInUpcomingCourt(player.id),
      sessionPlayerScheduledGames,
      partnershipLookup,
      playersById,
    });
    if (blockDesc) {
      return {
        menuKey: HOST_MENU_EXCLUDE_KEYS.remove,
        label: 'Remove from session',
        variant: 'danger',
        disabled: true,
        description: blockDesc,
      };
    }
    return {
      menuKey: HOST_MENU_EXCLUDE_KEYS.remove,
      label: 'Remove from session',
      variant: 'danger',
      action: () => setRemoveModalPlayer(player),
    };
  };

  const matchPreferenceMenuEligible =
    sessionId && ['active', 'signed_up', 'paused'].includes(status);

  const hostMaySetMatchPreferenceForPlayer = player?.is_guest === true;

  const mensEligibilityStatusEligible =
    sessionId && ['active', 'signed_up', 'paused'].includes(status);

  const isFemalePlayer = (player?.gender || '').toLowerCase() === 'female';

  const addEditPlayerMenuItem = () => {
    if (player.is_guest) {
      return {
        label: 'Edit player',
        action: () =>
          openPlayerInfoModal(player, {
            initialViewMode: 'edit',
            initialEditFocus: 'guest_profile',
          }),
      };
    }
    return {
      label: 'Edit player',
      disabled: true,
      title: EDIT_PLAYER_GUEST_ONLY_TOOLTIP,
      description: EDIT_PLAYER_GUEST_ONLY_TOOLTIP,
    };
  };

  const addMensEligibilityMenuItems = () => {
    if (hideMensDoublesEligibilityMenu) return [];
    if (!mensEligibilityStatusEligible) return [];
    if (isFemalePlayer) {
      return [
        {
          label: 'Mens doubles eligibility',
          action: () => setEligibilityModalPlayer(player),
        },
        { divider: true },
      ];
    }
    return [
      {
        label: 'Mens doubles eligibility',
        disabled: true,
        title: MENS_ELIGIBILITY_FEMALE_ONLY_TOOLTIP,
        description: MENS_ELIGIBILITY_FEMALE_ONLY_TOOLTIP,
      },
      { divider: true },
    ];
  };

  const addPreferenceMenuItems = () => {
    if (hideMatchPreferenceMenu) return [];
    if (!matchPreferenceMenuEligible) return [];
    if (!matchPreferencesEnabled) {
      return [
        {
          label: 'Add preference',
          disabled: true,
          title: PREF_DISABLED_TOOLTIP,
          description: PREF_DISABLED_TOOLTIP,
        },
        { divider: true },
      ];
    }
    if (!hostMaySetMatchPreferenceForPlayer) {
      return [
        {
          label: 'Add preference',
          disabled: true,
          title: PREF_MEMBER_SELF_SERVICE_TOOLTIP,
          description: PREF_MEMBER_SELF_SERVICE_TOOLTIP,
        },
        { divider: true },
      ];
    }
    return [
      {
        label: 'Add preference',
        action: () => setPreferenceModalPlayer(player),
      },
      { divider: true },
    ];
  };

  let items = [];

  if (status === 'active') {
    const onCourt = isOnActiveCourt(player.id);
    const topSection = [addEditPlayerMenuItem()];
    topSection.push(...addPreferenceMenuItems());
    topSection.push(...addMensEligibilityMenuItems());
    if (sessionId && !hideLateJoinPenaltyMenu) {
      topSection.push({
        label: 'Late Join Penalty',
        action: () =>
          openPlayerInfoModal(player, {
            initialViewMode: 'edit',
            initialEditFocus: 'session_penalty',
          }),
      });
    }

    items = onCourt
      ? [
          ...topSection,
          ...dividerBeforeStatusActions(topSection),
          { label: 'Finished', action: () => togglePlayerStatus(player.id, 'inactive', 'finished') },
          { divider: true },
          {
            disabled: true,
            menuKey: HOST_MENU_EXCLUDE_KEYS.pause,
            label: 'Pause unavailable',
            description: PAUSE_BLOCKED_ON_COURT_MSG,
          },
        ]
      : [
          ...topSection,
          ...dividerBeforeStatusActions(topSection),
          {
            menuKey: HOST_MENU_EXCLUDE_KEYS.pause,
            label: 'Pause',
            action: () => togglePlayerStatus(player.id, 'paused'),
          },
          { divider: true },
          buildRemoveMenuItem(),
        ];
  } else if (status === 'signed_up') {
    items = [
      {
        menuKey: HOST_MENU_EXCLUDE_KEYS.addToWaitlist,
        label: 'Add to queue',
        action: () => togglePlayerStatus(player.id, 'active'),
      },
      { divider: true },
      addEditPlayerMenuItem(),
      ...addPreferenceMenuItems(),
      ...addMensEligibilityMenuItems(),
      { label: 'No show', action: () => togglePlayerStatus(player.id, 'inactive', 'no_show') },
      { divider: true },
      {
        label: 'Cancelled',
        variant: 'danger',
        action: () => togglePlayerStatus(player.id, 'cancelled', 'cancelled'),
      },
      { divider: true },
      buildRemoveMenuItem(),
    ];
  } else if (status === 'paused') {
    items = [
      {
        menuKey: HOST_MENU_EXCLUDE_KEYS.resume,
        label: 'Resume',
        action: () => togglePlayerStatus(player.id, 'active'),
      },
      { divider: true },
      addEditPlayerMenuItem(),
      ...addPreferenceMenuItems(),
      ...addMensEligibilityMenuItems(),
      { divider: true },
      buildRemoveMenuItem(),
    ];
  } else {
    return [];
  }

  return normalizeMenuItems(filterExcludedItems(items, excludeKeys));
};

export const canHostRemovePlayerFromSession = (player, context) =>
  canRemovePlayerFromSession({
    player,
    isOnCourt:
      context.playersOnActiveCourts &&
      typeof context.playersOnActiveCourts.has === 'function' &&
      context.playersOnActiveCourts.has(player.id),
    isInUpcoming:
      context.playersInUpcomingCourts &&
      typeof context.playersInUpcomingCourts.has === 'function' &&
      context.playersInUpcomingCourts.has(player.id),
    sessionPlayerScheduledGames: context.sessionPlayerScheduledGames,
    partnershipLookup: context.partnershipLookup,
    playersById: context.playersById,
  });
